import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Calendar, DollarSign, X, CheckCircle, RefreshCw, Copy, ExternalLink, Unplug } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  return fallback;
};

const Orders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [],
    data_prevista: '',
  });
  const [selectedRecipe, setSelectedRecipe] = useState('');
  const [recipeQuantity, setRecipeQuantity] = useState('');
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [settlementMode, setSettlementMode] = useState('online');
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [manualMethod, setManualMethod] = useState('DINHEIRO');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerCpf, setPayerCpf] = useState('');
  const [payerAddress, setPayerAddress] = useState({
    zip_code: '', street_name: '', street_number: '', neighborhood: '', city: '', state: '',
  });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
    if (user?.role === 'Admin' && !user?.is_demo) fetchProviderStatus();
    const providerResult = searchParams.get('payment_provider');
    if (providerResult) {
      if (providerResult === 'connected') toast.success('Conta Mercado Pago conectada.');
      else toast.error('Não foi possível conectar a conta Mercado Pago. Tente novamente.');
      if (user?.role === 'Admin' && !user?.is_demo) fetchProviderStatus();
      setSearchParams({}, { replace: true });
    }
  }, []);

  const fetchProviderStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/provider/status`);
      setProviderStatus(response.data);
    } catch (error) {
      setProviderStatus({ configured: false, connected: false });
    }
  };

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, recipesRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/recipes`),
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setRecipes(recipesRes.data);
      return ordersRes.data;
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    if (!selectedRecipe || !recipeQuantity) {
      toast.error('Selecione uma receita e quantidade');
      return;
    }

    const recipe = recipes.find((r) => r.id === selectedRecipe);
    if (!recipe) return;

    const quantidade = parseInt(recipeQuantity);
    const valor_unitario = recipe.custo_unitario * 2.5; // 150% markup
    const valor_total = valor_unitario * quantidade;

    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          recipe_id: recipe.id,
          recipe_name: recipe.nome,
          quantidade,
          valor_unitario,
          valor_total,
        },
      ],
    });
    setSelectedRecipe('');
    setRecipeQuantity('');
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }

    try {
      const response = await axios.post(`${API}/orders`, {
        ...formData,
        data_prevista: new Date(formData.data_prevista).toISOString(),
      });
      toast.success('Pedido criado!');
      setDialogOpen(false);
      resetForm();
      fetchData();
      const newOrder = {
        ...response.data,
        customer_email: customers.find((customer) => customer.id === response.data.customer_id)?.email || null,
      };
      setTimeout(() => openPaymentDialog(newOrder, 'Sinal'), 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${encodeURIComponent(newStatus)}`);
      toast.success('Status atualizado!');
      const refreshedOrders = await fetchData();
      const order = refreshedOrders?.find((item) => item.id === orderId) || orders.find((item) => item.id === orderId);
      if (newStatus === 'Em produção' && order && !order.sinal_pago) {
        openPaymentDialog(order, 'Sinal');
      } else if (newStatus === 'Pronto' && order && order.saldo_devedor > 0) {
        openPaymentDialog(order, 'Saldo');
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const openPaymentDialog = (order, tipo) => {
    const creatingCharge = (order.pagamentos || []).find(
      (payment) => payment.status === 'creating' && payment.tipo === tipo,
    );
    const due = tipo === 'Sinal'
      ? Math.max((order.valor_total || 0) * 0.5 - (order.pagamentos || [])
        .filter((payment) => payment.status === 'paid' && ['Sinal', 'Completo'].includes(payment.tipo))
        .reduce((sum, payment) => sum + payment.valor, 0), 0)
      : (order.saldo_devedor ?? order.valor_total ?? 0);
    setPaymentDialog({ orderId: order.id, tipo, order });
    setPaymentAmount(Number(due || 0).toFixed(2));
    setSettlementMode(user?.is_demo ? 'manual' : 'online');
    setPayerEmail(order.customer_email || customers.find((customer) => customer.id === order.customer_id)?.email || '');
    setPaymentMethod(creatingCharge?.metodo || 'PIX');
    setManualMethod('DINHEIRO');
    setPayerCpf('');
    setPayerAddress({ zip_code: '', street_name: '', street_number: '', neighborhood: '', city: '', state: '' });
  };

  const connectMercadoPago = async () => {
    try {
      const response = await axios.post(`${API}/payments/provider/connect`);
      window.location.assign(response.data.authorization_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível iniciar a conexão Mercado Pago');
    }
  };

  const disconnectMercadoPago = async () => {
    setPaymentBusy(true);
    try {
      await axios.delete(`${API}/payments/provider/connection`);
      await fetchProviderStatus();
      toast.success('Conta Mercado Pago desconectada.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível desconectar a conta Mercado Pago');
    } finally {
      setPaymentBusy(false);
    }
  };

  const handleOnlineCharge = async () => {
    if (!paymentDialog) return;
    setPaymentBusy(true);
    try {
      const payer = { email: payerEmail.trim() || null };
      if (paymentMethod === 'BOLETO') {
        payer.cpf = payerCpf.trim() || null;
        Object.entries(payerAddress).forEach(([key, value]) => {
          payer[key] = value.trim() || null;
        });
      }
      await axios.post(`${API}/payments/charges`, {
        order_id: paymentDialog.orderId,
        tipo: paymentDialog.tipo,
        metodo: paymentMethod,
        payer,
      });
      toast.success(paymentMethod === 'PIX' ? 'Cobrança Pix criada.' : 'Boleto gerado.');
      setPaymentDialog(null);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível gerar a cobrança'));
    } finally {
      setPaymentBusy(false);
    }
  };

  const handleManualPayment = async () => {
    if (!paymentDialog) return;
    setPaymentBusy(true);
    try {
      await axios.post(`${API}/payments`, {
        order_id: paymentDialog.orderId,
        tipo: paymentDialog.tipo,
        metodo: manualMethod,
        valor: Number(paymentAmount),
      });
      toast.success('Recebimento registrado.');
      setPaymentDialog(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível registrar o recebimento');
    } finally {
      setPaymentBusy(false);
    }
  };

  const cancelCharge = async (paymentId) => {
    setPaymentBusy(true);
    try {
      await axios.post(`${API}/payments/${paymentId}/cancel`);
      toast.success('Cobrança pendente cancelada.');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível cancelar a cobrança');
    } finally {
      setPaymentBusy(false);
    }
  };

  const refreshCharge = async (paymentId) => {
    setPaymentBusy(true);
    try {
      await axios.post(`${API}/payments/${paymentId}/refresh`);
      await fetchData();
      toast.success('Status da cobrança atualizado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível consultar a cobrança');
    } finally {
      setPaymentBusy(false);
    }
  };

  const sharePayment = async (payment) => {
    const text = payment.metodo === 'PIX'
      ? `Pix do pedido: ${payment.pix_qr_code || payment.external_resource_url || ''}`
      : `Boleto do pedido: ${payment.external_resource_url || payment.boleto_digitable_line || ''}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Pagamento do pedido', text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success('Dados de pagamento copiados.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') toast.error('Não foi possível compartilhar os dados de pagamento.');
    }
  };

  const copyPaymentDetails = async (payment) => {
    const value = payment.metodo === 'PIX'
      ? payment.pix_qr_code
      : payment.boleto_digitable_line || payment.external_resource_url;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(payment.metodo === 'PIX' ? 'Código Pix copiado.' : 'Dados do boleto copiados.');
    } catch (error) {
      toast.error('Não foi possível copiar os dados de pagamento.');
    }
  };

  const resetForm = () => {
    setFormData({ customer_id: '', items: [], data_prevista: '' });
    setSelectedRecipe('');
    setRecipeQuantity('');
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  const isAdmin = user?.role === 'Admin';

  const statusColors = {
    'Em produção': 'bg-blue-100 text-blue-800',
    Pronto: 'bg-green-100 text-green-800',
    Enviado: 'bg-purple-100 text-purple-800',
    Pago: 'bg-emerald-100 text-emerald-800',
  };
  const paymentStatusLabels = {
    creating: 'Criando', pending: 'Pendente', in_process: 'Em análise',
    action_required: 'Ação necessária', paid: 'Pago', cancelled: 'Cancelado',
    expired: 'Expirado', rejected: 'Recusado', failed: 'Falhou',
    refunded: 'Estornado', charged_back: 'Contestação',
  };
  const activePaymentStatuses = ['creating', 'pending', 'in_process', 'action_required'];

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent mb-2">
            Pedidos
          </h1>
          <p className="text-gray-600">Gerencie seus pedidos e pagamentos</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500" data-testid="add-order-button">
                <Plus className="mr-2 h-4 w-4" />
                Novo Pedido
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
              <DialogHeader>
                <DialogTitle>Novo Pedido</DialogTitle>
                <DialogDescription>Crie um novo pedido</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_id">Cliente</Label>
                  <Select value={formData.customer_id} onValueChange={(val) => setFormData({ ...formData, customer_id: val })} required>
                    <SelectTrigger data-testid="select-customer">
                      <SelectValue placeholder="Selecione cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_prevista">Data Prevista de Entrega</Label>
                  <Input
                    id="data_prevista"
                    type="date"
                    value={formData.data_prevista}
                    onChange={(e) => setFormData({ ...formData, data_prevista: e.target.value })}
                    required
                    data-testid="order-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Itens do Pedido</Label>
                  <div className="flex gap-2">
                    <Select value={selectedRecipe} onValueChange={setSelectedRecipe}>
                      <SelectTrigger className="flex-1" data-testid="select-recipe">
                        <SelectValue placeholder="Selecione receita" />
                      </SelectTrigger>
                      <SelectContent>
                        {recipes.map((recipe) => (
                          <SelectItem key={recipe.id} value={recipe.id}>
                            {recipe.nome} (R$ {(recipe.custo_unitario * 2.5).toFixed(2)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Qtd"
                      value={recipeQuantity}
                      onChange={(e) => setRecipeQuantity(e.target.value)}
                      className="w-24"
                      data-testid="recipe-quantity-input"
                    />
                    <Button type="button" onClick={addItem} size="sm" data-testid="add-item-to-order">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.items.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-pink-50 p-3 rounded" data-testid={`order-item-${index}`}>
                          <div>
                            <p className="font-semibold text-sm">{item.recipe_name}</p>
                            <p className="text-xs text-gray-600">
                              {item.quantidade}x R$ {item.valor_unitario.toFixed(2)} = R$ {item.valor_total.toFixed(2)}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            data-testid={`remove-item-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="bg-green-50 p-3 rounded font-semibold">
                        Total: R$ {formData.items.reduce((sum, item) => sum + item.valor_total, 0).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400" data-testid="order-submit-button">
                  Criar Pedido
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isAdmin && !user?.is_demo && providerStatus && (
        <Card className="border-blue-100 bg-white/80">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <p className="font-semibold">Recebimentos online</p>
              <p className="text-sm text-gray-600">
                {providerStatus.connected
                  ? `Mercado Pago conectado${providerStatus.account_id ? ` (conta ${providerStatus.account_id})` : ''}.`
                  : providerStatus.configured
                    ? 'Conecte a conta Mercado Pago desta confeitaria para gerar Pix e boletos.'
                    : 'A integração Mercado Pago ainda não foi configurada no servidor.'}
              </p>
            </div>
            {providerStatus.configured && (
              <Button
                size="sm"
                variant={providerStatus.connected ? 'outline' : 'default'}
                disabled={paymentBusy}
                onClick={providerStatus.connected ? disconnectMercadoPago : connectMercadoPago}
              >
                {providerStatus.connected ? <><Unplug className="mr-2 h-4 w-4" />Desconectar</> : 'Conectar Mercado Pago'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {orders.length === 0 ? (
        <Card className="border-pink-100 bg-white/80 backdrop-blur-sm" data-testid="no-orders">
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhum pedido cadastrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const dataPrevista = new Date(order.data_prevista);
            const isOverdue = dataPrevista < new Date() && order.status !== 'Pago';

            return (
              <Card key={order.id} className={`border-pink-100 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow ${
                isOverdue ? 'ring-2 ring-orange-300' : ''
              }`} data-testid={`order-card-${order.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{order.customer_name}</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Pedido #{order.id.slice(0, 8)}</p>
                    </div>
                    <Badge className={statusColors[order.status]} data-testid={`order-status-${order.id}`}>{order.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Data Prevista
                      </p>
                      <p className="font-semibold text-sm">
                        {format(dataPrevista, 'dd/MM/yyyy', { locale: ptBR })}
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Valor Total
                      </p>
                      <p className="font-semibold text-sm">R$ {order.valor_total.toFixed(2)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Itens:</p>
                    <ul className="space-y-1">
                      {order.items.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600">
                          • {item.recipe_name}: {item.quantidade}x R$ {item.valor_unitario.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="rounded-lg bg-emerald-50 p-3">
                      <p className="text-xs text-gray-600">Recebido</p>
                      <p className="font-semibold">R$ {(order.valor_pago || 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 p-3">
                      <p className="text-xs text-gray-600">Cobranças pendentes</p>
                      <p className="font-semibold">R$ {(order.valor_pendente || 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs text-gray-600">Saldo a receber</p>
                      <p className="font-semibold">R$ {(order.saldo_devedor || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm font-semibold">Parcelas:</span>
                    <Badge variant={order.sinal_pago ? 'default' : 'outline'} className="text-xs">
                      Sinal (50%) {order.sinal_pago && <CheckCircle className="w-3 h-3 ml-1 inline" />}
                    </Badge>
                    <Badge variant={order.saldo_pago ? 'default' : 'outline'} className="text-xs">
                      Saldo {order.saldo_pago && <CheckCircle className="w-3 h-3 ml-1 inline" />}
                    </Badge>
                  </div>
                  {(order.pagamentos || []).length > 0 && (
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-sm font-semibold text-gray-700">Histórico de pagamentos</p>
                      {order.pagamentos.map((payment) => {
                        const isActive = activePaymentStatuses.includes(payment.status);
                        return (
                          <div key={payment.id} className="rounded-lg border p-3 space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm">
                                {payment.tipo} · {payment.metodo?.replaceAll('_', ' ')} · R$ {payment.valor.toFixed(2)}
                              </p>
                              <Badge variant={payment.status === 'paid' ? 'default' : 'outline'}>
                                {paymentStatusLabels[payment.status] || payment.status}
                              </Badge>
                            </div>
                            {isActive && payment.origem === 'mercado_pago' && (
                              <div className="space-y-2 text-sm">
                                {payment.pix_qr_code_base64 && (
                                  <img
                                    className="h-36 w-36 rounded border bg-white p-1"
                                    src={`data:image/png;base64,${payment.pix_qr_code_base64}`}
                                    alt="QR Code Pix"
                                  />
                                )}
                                {payment.pix_qr_code && (
                                  <p className="break-all rounded bg-gray-50 p-2 font-mono text-xs">{payment.pix_qr_code}</p>
                                )}
                                {payment.boleto_digitable_line && (
                                  <p className="break-all rounded bg-gray-50 p-2 font-mono text-xs">{payment.boleto_digitable_line}</p>
                                )}
                                {payment.expires_at && (
                                  <p className="text-xs text-gray-500">
                                    Vencimento: {format(new Date(payment.expires_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  {payment.status === 'creating' && isAdmin && (
                                    <Button size="sm" variant="outline" onClick={() => openPaymentDialog(order, payment.tipo)}>
                                      Continuar geração
                                    </Button>
                                  )}
                                  {(payment.pix_qr_code || payment.boleto_digitable_line) && (
                                    <Button size="sm" variant="outline" onClick={() => copyPaymentDetails(payment)}>
                                      <Copy className="mr-1 h-3 w-3" />Copiar código
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => sharePayment(payment)}>
                                    Compartilhar
                                  </Button>
                                  {payment.external_resource_url && (
                                    <Button size="sm" variant="outline" asChild>
                                      <a href={payment.external_resource_url} target="_blank" rel="noreferrer">
                                        <ExternalLink className="mr-1 h-3 w-3" />Abrir cobrança
                                      </a>
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" disabled={paymentBusy} onClick={() => refreshCharge(payment.id)}>
                                    <RefreshCw className="mr-1 h-3 w-3" />Atualizar status
                                  </Button>
                                  <Button size="sm" variant="outline" disabled={paymentBusy} onClick={() => cancelCharge(payment.id)}>
                                    Cancelar cobrança
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {order.status !== 'Pago' && (
                        <>
                          <Select onValueChange={(val) => updateStatus(order.id, val)}>
                            <SelectTrigger className="w-40" data-testid={`change-status-${order.id}`}>
                              <SelectValue placeholder="Alterar status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Em produção">Em produção</SelectItem>
                              <SelectItem value="Pronto">Pronto</SelectItem>
                              <SelectItem value="Enviado">Enviado</SelectItem>
                            </SelectContent>
                          </Select>
                          {!order.saldo_pago && !(order.pagamentos || []).some((payment) => activePaymentStatuses.includes(payment.status)) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPaymentDialog(order, order.sinal_pago ? 'Saldo' : 'Sinal')}
                              className="border-green-300 hover:bg-green-50"
                              data-testid={`record-payment-${order.id}`}
                            >
                              Registrar recebimento
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={Boolean(paymentDialog)} onOpenChange={(open) => { if (!open) setPaymentDialog(null); }}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {paymentDialog?.tipo === 'Sinal' ? 'Receber sinal do pedido' : 'Receber saldo do pedido'}
            </DialogTitle>
            <DialogDescription>
              Pedido #{paymentDialog?.orderId?.slice(0, 8)}. Gerar uma cobrança online deixa o valor pendente até a confirmação do provedor.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="settlement-mode">Como o cliente vai pagar?</Label>
              <Select value={settlementMode} onValueChange={setSettlementMode}>
                <SelectTrigger id="settlement-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!user?.is_demo && <SelectItem value="online">Mercado Pago (online)</SelectItem>}
                  <SelectItem value="manual">Pagamento já recebido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {settlementMode === 'online' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="online-method">Forma de cobrança</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger id="online-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PIX">Pix</SelectItem>
                      <SelectItem value="BOLETO">Boleto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payer-email">Email do pagador</Label>
                  <Input id="payer-email" type="email" required value={payerEmail} onChange={(event) => setPayerEmail(event.target.value)} />
                </div>
                {paymentMethod === 'BOLETO' && (
                  <div className="grid grid-cols-2 gap-3 rounded-lg border p-3">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="payer-cpf">CPF do pagador</Label>
                      <Input id="payer-cpf" inputMode="numeric" value={payerCpf} onChange={(event) => setPayerCpf(event.target.value)} placeholder="000.000.000-00" required />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="payer-street">Rua</Label>
                      <Input id="payer-street" value={payerAddress.street_name} onChange={(event) => setPayerAddress({ ...payerAddress, street_name: event.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payer-street-number">Número</Label>
                      <Input id="payer-street-number" value={payerAddress.street_number} onChange={(event) => setPayerAddress({ ...payerAddress, street_number: event.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payer-neighborhood">Bairro</Label>
                      <Input id="payer-neighborhood" value={payerAddress.neighborhood} onChange={(event) => setPayerAddress({ ...payerAddress, neighborhood: event.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payer-city">Cidade</Label>
                      <Input id="payer-city" value={payerAddress.city} onChange={(event) => setPayerAddress({ ...payerAddress, city: event.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="payer-state">UF</Label>
                      <Input id="payer-state" maxLength={2} value={payerAddress.state} onChange={(event) => setPayerAddress({ ...payerAddress, state: event.target.value.toUpperCase() })} required />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="payer-zip">CEP</Label>
                      <Input id="payer-zip" inputMode="numeric" value={payerAddress.zip_code} onChange={(event) => setPayerAddress({ ...payerAddress, zip_code: event.target.value })} required />
                    </div>
                    <p className="col-span-2 text-xs text-gray-500">Os dados são enviados ao Mercado Pago para emitir o boleto.</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="charge-amount">Valor da cobrança</Label>
                  <Input id="charge-amount" type="number" step="0.01" value={paymentAmount} readOnly />
                  <p className="text-xs text-gray-500">O valor é calculado pelo saldo real da parcela e não será marcado como recebido até a confirmação.</p>
                </div>
                {!providerStatus?.connected && (
                  <p className="rounded bg-amber-50 p-3 text-sm text-amber-800">
                    Conecte primeiro a conta Mercado Pago desta confeitaria no painel de Recebimentos online.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="manual-method">Forma recebida</Label>
                  <Select value={manualMethod} onValueChange={setManualMethod}>
                    <SelectTrigger id="manual-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                      <SelectItem value="CARTAO_MAQUININHA">Cartão na maquininha</SelectItem>
                      <SelectItem value="PIX_EXTERNO">Pix recebido fora do sistema</SelectItem>
                      <SelectItem value="OUTRO">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="manual-amount">Valor recebido</Label>
                  <Input id="manual-amount" type="number" min="0.01" max={paymentDialog?.order?.saldo_devedor || paymentDialog?.order?.valor_total} step="0.01" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} required />
                  <p className="text-xs text-gray-500">O valor informado será registrado como recebido agora.</p>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPaymentDialog(null)} disabled={paymentBusy}>Fazer depois</Button>
              <Button
                type="button"
                disabled={paymentBusy || (settlementMode === 'online' && !providerStatus?.connected)}
                onClick={settlementMode === 'online' ? handleOnlineCharge : handleManualPayment}
              >
                {paymentBusy ? 'Processando…' : settlementMode === 'online' ? 'Gerar cobrança' : 'Confirmar recebimento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
